import { SeriesInstanceUid } from '~/domain/dicom/uids';

/**
 * Layout model for the viewport grid.
 *
 * The user-facing mental model is a matrix: a row is one dataset, a cell is
 * one pane at a stable `R·C` address. Each row owns a track grid, and every
 * pane is placed in it CSS-grid style, so a row can hang three even planes,
 * one large plane beside two stacked ones, or a 2x2 quad without a new code
 * path. A named template is therefore data, not a branch.
 *
 * The two-level tree (`Layout` -> `DatasetRow` -> `ViewportCell`) keeps the
 * "a row never mixes datasets" invariant true by construction. Fully
 * free-form recursive splitting stays out of scope.
 */

/** Stable identity + persistence key for one viewport cell. */
export type ViewportId = string & { readonly __brand: 'ViewportId' };

export enum Plane {
    Transversal = 'transversal',
    Sagittal = 'sagittal',
    Coronal = 'coronal',
}

/**
 * Which image a plane pane draws. A row holds one dataset, so `secondary`
 * means the fused secondary of that row's own Environment, not another
 * row's dataset. `blended` is the composite the Environment renders.
 */
export enum ImageLayer {
    Blended = 'blended',
    Primary = 'primary',
    Secondary = 'secondary',
}

const PANEL_KINDS = ['dvh', 'dose-statistics'] as const;

/**
 * A pane that shows a panel rather than a plane: the dose-volume histogram,
 * or the dose statistics table. Each kind hangs at most once in the grid,
 * in the row of the image the panel belongs to.
 */
export type PanelKind = (typeof PANEL_KINDS)[number];

/** What one pane shows. A plane of the row's image, or a panel. */
export type PaneContent =
    | {
          readonly kind: 'plane';
          readonly plane: Plane;
          readonly layer: ImageLayer;
      }
    | { readonly kind: PanelKind };

/** The panel pane of one kind. */
export function panelContent(kind: PanelKind): PaneContent {
    return { kind };
}

/** Whether a string names a panel kind. */
export function isPanelKind(value: string): value is PanelKind {
    return (PANEL_KINDS as ReadonlyArray<string>).includes(value);
}

/** A plane pane of the row's blended image, the common case. */
export function planeContent(
    plane: Plane,
    layer: ImageLayer = ImageLayer.Blended
): PaneContent {
    return { kind: 'plane', plane, layer };
}

/**
 * A pane's placement in its row's track grid, 1-based and inclusive of the
 * start track, matching CSS grid's `grid-column: start / span n`.
 */
export interface CellPlacement {
    readonly colStart: number;
    readonly colSpan: number;
    readonly rowStart: number;
    readonly rowSpan: number;
}

/**
 * The one dataset/series bound to a row: its series key only. What the row
 * shows of the series (its name, modality and frame of reference) is read
 * from the dataset adapter each time, so it never goes stale. The key is
 * not for display or logs.
 */
export interface DatasetRef {
    readonly seriesInstanceUid: SeriesInstanceUid;
}

/** One viewport: a pane of its row's dataset, placed in the row's tracks. */
export interface ViewportCell {
    readonly id: ViewportId;
    readonly content: PaneContent;
    readonly placement: CellPlacement;
}

/**
 * A row's shape, independent of which dataset fills it: the track sizes and
 * the panes placed in them. This is what a named layout template carries.
 */
export interface RowTemplate {
    /** Column track sizes, in percent of the row's width. */
    readonly columnPercents: ReadonlyArray<number>;
    /** Row track sizes, in percent of the row's height. */
    readonly rowPercents: ReadonlyArray<number>;
    readonly panes: ReadonlyArray<PaneSpec>;
}

/** One pane of a template: what it shows and where it sits. */
export interface PaneSpec {
    readonly content: PaneContent;
    readonly placement: CellPlacement;
}

/**
 * One row to hang: a dataset paired with the shape it hangs, or `undefined`
 * for an empty placeholder awaiting a binding. Composition roots build the
 * opening layout from an ordered list of these; the same spec inserts a row.
 */
export interface GridRowSpec {
    readonly dataset: DatasetRef | undefined;
    readonly template: RowTemplate;
}

/**
 * The primary row: the one that shows the dataset the sidebar names. It is
 * always the first row, it cannot be closed, and its image changes only
 * through the sidebar, so the grid and the sidebar never disagree about what
 * is being worked on. Every other row is the user's own.
 */
export const PRIMARY_ROW_INDEX = 0;

/**
 * The band of panels a row hangs under its image panes: a track grid of
 * its own, one column per panel, taking a share of the row's height above
 * the row's footer.
 */
export interface PanelBand {
    /** The band's share of the row's height, in percent. */
    readonly heightPercent: number;
    /** Column track sizes, in percent; length is the band's panel count. */
    readonly columnPercents: ReadonlyArray<number>;
}

/**
 * One dataset row. Binds exactly one dataset, or `undefined` when the row
 * is an empty placeholder awaiting a binding. A row never mixes datasets.
 */
export interface DatasetRow {
    readonly dataset: DatasetRef | undefined;
    /** Proportional height within the grid, in percent. */
    readonly heightPercent: number;
    /** Column track sizes of the image panes, in percent; length is the column count. */
    readonly columnPercents: ReadonlyArray<number>;
    /** Row track sizes of the image panes, in percent; length is the sub-row count. */
    readonly rowPercents: ReadonlyArray<number>;
    /**
     * Panes in reading order: the image panes left to right, then top to
     * bottom, placed in the row's tracks; then the band's panels left to
     * right, placed in the band's tracks.
     */
    readonly cells: ReadonlyArray<ViewportCell>;
    /** The band of panels under the image panes, while the row hangs any. */
    readonly band?: PanelBand;
}

/** The whole grid: an ordered, top-to-bottom stack of dataset rows. */
export interface Layout {
    readonly rows: ReadonlyArray<DatasetRow>;
}

/** Mints stable viewport ids; injected so the model stays deterministic. */
export type ViewportIdFactory = () => ViewportId;
